/**
 * Number of applications by five underwriting/ re-underwriting results, by age and sex of Insured Person
 */

import dayjs from "dayjs"
import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"

import {
	countAccpetedRejectedOffersMatchingPlan,
	countAccpetedRejectedOffersMatchingPlanWithLoadingsOnly,
	countAccpetedRejectedOffersMatchingPlanWithExclusionsOnly,
	countAccpetedRejectedOffersMatchingPlanWithLoadingsAndExclusions
} from "./helpers.js"

const header = [
	[
		"Template A1 - Number of accepted/rejected offer of customers by four Underwriting/ Re-underwriting results, by age and sex of Insured Person"
	],
	["Age", "Sex", "Standard Plan", "", "", "", "", "", "", "", "Flexi Plan"],
	[
		"",
		"",
		"Standard Offer",
		"",
		"Offer with Premium Loading Only",
		"",
		"Offer with CBE only",
		"",
		"Offer with Premium Loading and CBE",
		"",
		"Standard Offer",
		"",
		"Offer with Premium Loading Only",
		"",
		"Offer with CBE only",
		"",
		"Offer with Premium Loading and CBE",
		""
	],
	[
		"",
		"",
		"Accept",
		"Reject",
		"Accept",
		"Reject",
		"Accept",
		"Reject",
		"Accept",
		"Reject",
		"Accept",
		"Reject",
		"Accept",
		"Reject",
		"Accept",
		"Reject",
		"Accept",
		"Reject"
	]
]

// Return calculated age
const getAge = item => {
	const dateOfBirth = item?.insureds?.[0]?.dateOfBirth
	const currentYear = dayjs().year()
	const yearOfBirth = dayjs(dateOfBirth).year()
	return currentYear - yearOfBirth
}

const rows = payload => {
	let output = []
	for (let i = 1; i < 100; i++) {
		output.push([
			i,
			"M",
			countStandardOffer(i, "male", payload, "S00027", true),
			countStandardOffer(i, "male", payload, "S00027", false),
			countOfferWithLoadings(i, "male", payload, "S00027", true),
			countOfferWithLoadings(i, "male", payload, "S00027", false),
			countOfferWithExclusions(i, "male", payload, "S00027", true),
			countOfferWithExclusions(i, "male", payload, "S00027", false),
			countOfferWithLoadingsAndExclusions(i, "male", payload, "S00027", true),
			countOfferWithLoadingsAndExclusions(i, "male", payload, "S00027", false),
			countStandardOffer(i, "male", payload, "F00038", true),
			countStandardOffer(i, "male", payload, "F00038", false),
			countOfferWithLoadings(i, "male", payload, "F00038", true),
			countOfferWithLoadings(i, "male", payload, "F00038", false),
			countOfferWithExclusions(i, "male", payload, "F00038", true),
			countOfferWithExclusions(i, "male", payload, "F00038", false),
			countOfferWithLoadingsAndExclusions(i, "male", payload, "F00038", true),
			countOfferWithLoadingsAndExclusions(i, "male", payload, "F00038", false)
		])
	}
	for (let j = 1; j < 100; j++) {
		output.push([
			j,
			"F",
			countStandardOffer(j, "female", payload, "S00027", true),
			countStandardOffer(j, "female", payload, "S00027", false),
			countOfferWithLoadings(j, "female", payload, "S00027", true),
			countOfferWithLoadings(j, "female", payload, "S00027", false),
			countOfferWithExclusions(j, "female", payload, "S00027", true),
			countOfferWithExclusions(j, "female", payload, "S00027", false),
			countOfferWithLoadingsAndExclusions(j, "female", payload, "S00027", true),
			countOfferWithLoadingsAndExclusions(
				j,
				"female",
				payload,
				"S00027",
				false
			),
			countStandardOffer(j, "female", payload, "F00038", true),
			countStandardOffer(j, "female", payload, "F00038", false),
			countOfferWithLoadings(j, "female", payload, "F00038", true),
			countOfferWithLoadings(j, "female", payload, "F00038", false),
			countOfferWithExclusions(j, "female", payload, "F00038", true),
			countOfferWithExclusions(j, "female", payload, "F00038", false),
			countOfferWithLoadingsAndExclusions(j, "female", payload, "F00038", true),
			countOfferWithLoadingsAndExclusions(j, "female", payload, "F00038", false)
		])
	}
	return output
}

const countStandardOffer = (age, gender, cases, plan, isAccepted) => {
	const filteredCases = cases.map(item => {
		let insuredAge = getAge(item)
		let insuredGender = item?.insureds?.[0]?.gender
		let offersCount = countAccpetedRejectedOffersMatchingPlan(
			item?.proposals,
			plan,
			isAccepted
		)
		if (insuredAge === age && insuredGender === gender) {
			return offersCount
		} else return 0
	})
	return filteredCases?.reduce((total, item) => {
		return (total += item)
	}, 0)
}

const countOfferWithLoadings = (age, gender, cases, plan, isAccepted) => {
	const filteredCases = cases.map(item => {
		let insuredAge = getAge(item)
		let insuredGender = item?.insureds?.[0]?.gender
		let offersCount = countAccpetedRejectedOffersMatchingPlanWithLoadingsOnly(
			item?.proposals,
			plan,
			isAccepted
		)

		if (insuredAge === age && insuredGender === gender) {
			return offersCount
		} else return 0
	})
	return filteredCases?.reduce((total, item) => {
		return (total += item)
	}, 0)
}

const countOfferWithExclusions = (age, gender, cases, plan, isAccepted) => {
	const filteredCases = cases.map(item => {
		let insuredAge = getAge(item)
		let insuredGender = item?.insureds?.[0]?.gender
		let offersCount = countAccpetedRejectedOffersMatchingPlanWithExclusionsOnly(
			item?.proposals,
			plan,
			isAccepted
		)
		if (insuredAge === age && insuredGender === gender) {
			return offersCount
		} else return 0
	})
	return filteredCases?.reduce((total, item) => {
		return (total += item)
	}, 0)
}

const countOfferWithLoadingsAndExclusions = (
	age,
	gender,
	cases,
	plan,
	isAccepted
) => {
	const filteredCases = cases.map(item => {
		let insuredAge = getAge(item)
		let insuredGender = item?.insureds?.[0]?.gender
		let offersCount = countAccpetedRejectedOffersMatchingPlanWithLoadingsAndExclusions(
			item?.proposals,
			plan,
			isAccepted
		)
		if (insuredAge === age && insuredGender === gender) {
			return offersCount
		} else return 0
	})
	return filteredCases?.reduce((total, item) => {
		return (total += item)
	}, 0)
}

const fetchData = async filterPayload => {
	const query = gql`
		query fetchTemplateA1Payload($caseWhere: caseWhere) {
			cases(where: $caseWhere) {
				list {
					id
					insureds {
						... on individual {
							dateOfBirth
							gender
						}
					}
					proposals {
						id
						status
						basket {
							product {
								productId {
									plan
								}
							}
							premium {
								loadings {
									id
								}
							}
							exclusions {
								id
							}
						}
						policies {
							id
						}
					}
				}
			}
		}
	`
	const variables = {
		caseWhere: filterPayload
	}

	const res = await api({ query, variables })

	return res?.data?.cases?.list
}

export default async filterPayload => {
	const fetchedData = await fetchData(filterPayload)
	const workSheetData = [...header, ...rows(fetchedData)]
	const workSheetColumns = header[2].map(item => {
		return { wpx: 100 }
	})
	const workSheetMerges = [
		{
			s: { r: 0, c: 0 },
			e: { r: 0, c: 6 }
		},
		{
			s: { r: 1, c: 0 },
			e: { r: 3, c: 0 }
		},
		{
			s: { r: 1, c: 1 },
			e: { r: 3, c: 1 }
		},
		{
			s: { r: 1, c: 2 },
			e: { r: 1, c: 9 }
		},
		{
			s: { r: 1, c: 10 },
			e: { r: 1, c: 17 }
		},
		{
			s: { r: 2, c: 2 },
			e: { r: 2, c: 3 }
		},
		{
			s: { r: 2, c: 4 },
			e: { r: 2, c: 5 }
		},
		{
			s: { r: 2, c: 6 },
			e: { r: 2, c: 7 }
		},
		{
			s: { r: 2, c: 8 },
			e: { r: 2, c: 9 }
		},
		{
			s: { r: 2, c: 10 },
			e: { r: 2, c: 11 }
		},
		{
			s: { r: 2, c: 12 },
			e: { r: 2, c: 13 }
		},
		{
			s: { r: 2, c: 14 },
			e: { r: 2, c: 15 }
		},
		{
			s: { r: 2, c: 16 },
			e: { r: 2, c: 17 }
		}
	]
	return { workSheetData, workSheetColumns, workSheetMerges }
}
